/**
 * Guest discovery API client (#1903 backend, #1921 J2/J3 frontend).
 *
 * Both endpoints are PUBLIC — no auth, no tenant context (guest catalogue
 * is cross-org) — so this uses plain `safeFetch`, the same pattern
 * `HomePageV2.jsx` already uses for `GET /api/demo/scenarios/preview`, not
 * `safeAuthFetch`.
 */
import { safeFetch } from '../../utils/safeFetch';
import { API_BASE_URL } from '../../config/api';

/**
 * GET /api/demo/discovery/contexts — J2 context picker data: the context
 * chips (with dynamic counts), the editorial start-here card, and the
 * dynamic "Browse all" count.
 *
 * @returns {Promise<{contexts: Array<{slug:string,label:string,count:number}>, startHere: object|null, browseAllCount: number}>}
 */
export async function fetchDiscoveryContexts() {
  const res = await safeFetch(`${API_BASE_URL}/api/demo/discovery/contexts`);
  const data = await res.json();
  if (!data?.success) throw new Error('discovery contexts: unexpected response shape');
  return {
    contexts: Array.isArray(data.contexts) ? data.contexts : [],
    startHere: data.start_here ?? null,
    browseAllCount: Number.isInteger(data.browse_all_count) ? data.browse_all_count : 0,
  };
}

/**
 * GET /api/demo/discovery/context/:slug/shortlist — J3 shortlist: up to 3
 * cards for the given context, already ordered by the #1903 v0 metric with
 * the score-floor guard applied server-side.
 *
 * @param {string} contextSlug
 * @returns {Promise<Array<object>>} scenario card objects (may be shorter
 *   than 3, or empty, when a context has fewer eligible scenarios)
 */
export async function fetchDiscoveryShortlist(contextSlug) {
  const res = await safeFetch(
    `${API_BASE_URL}/api/demo/discovery/context/${encodeURIComponent(contextSlug)}/shortlist`,
    { allowErrorStatuses: [404] }
  );
  if (res.status === 404) return [];
  const data = await res.json();
  if (!data?.success) throw new Error('discovery shortlist: unexpected response shape');
  return Array.isArray(data.scenarios) ? data.scenarios : [];
}
